import { create } from 'zustand';
import { User } from '../types/auth.model';
import Cookies from 'js-cookie';
import { encryptData } from '../lib/utils';
import { CustomFormData } from '../types';

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null;
  login: (token: string, user: User) => void;
  /** Refreshes the cached user without touching the token or the session. */
  setUser: (user: User) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => {
  // Try to get token from cookies when initializing
  const tokenFromCookies = Cookies.get('token');
  const userDetails = localStorage.getItem('user');
  const initialState: AuthState = {
    isAuthenticated: !!tokenFromCookies, // Check if token exists in cookies
    token: tokenFromCookies || null,
    user: userDetails ? JSON.parse(userDetails) : null,
    login: (token: string, user: User) => {
      // Set token to cookies when logging in
      Cookies.set('token', encryptData(token), { expires: 1 });
      localStorage.setItem('user', JSON.stringify(user));
      set({ isAuthenticated: true, token, user });
    },
    setUser: (user: User) => {
      localStorage.setItem('user', JSON.stringify(user));
      set({ user });
    },
    logout: () => {
      // Remove token from cookies when logging out
      Cookies.remove('token');
      localStorage.clear();
      set({ isAuthenticated: false, token: null, user: null });
    },
  };
  return initialState;
});

type SideNavState = {
  isCollapse: boolean;
  setCollapse: (collapse: boolean) => void;
  selectedKey: string;
  setSelectedKey: (value: string) => void;
  selectedPage: string;
  setSelectedPage: (value: string) => void;
};

export const useSideNavStore = create<SideNavState>((set) => ({
  isCollapse: false,
  setCollapse: (collapse: boolean) => {
    set(() => ({ isCollapse: collapse }));
  },
  selectedKey: '/',
  setSelectedKey: (value: string) => {
    set(() => ({ selectedKey: value }));
  },
  selectedPage: 'home',
  setSelectedPage: (value: string) => {
    set(() => ({ selectedPage: value }));
  },
}));

type DrawerState = {
  isOpen: boolean;
  onClose: () => void;
};
export const useDrawerStore = create<DrawerState>((set) => ({
  isOpen: false,
  onClose: () => {
    set((state) => ({ isOpen: !state.isOpen }));
  },
}));

interface FormState {
  formData: CustomFormData;
  setFormData: (data: CustomFormData) => void;
  resetForm: () => void;
}

export const useFormStore = create<FormState>((set) => ({
  formData: {},
  setFormData: (data) => set({ formData: { ...data } }),
  resetForm: () => set({ formData: {} }),
}));

type StateSetter<T> = (data: T) => void;

type GenericState<T> = {
  data: T | null;
  setData: StateSetter<T | null>;
};

export const useGenericStore = <T>(initialData: T | null) =>
  create<GenericState<T>>((set) => ({
    data: initialData,
    setData: (data) => set({ data }),
  }));
